import React, { useEffect, useState } from 'react';

import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import type { AssociateProfilePlacementsResponse } from 'src/services/associateProfile/types';
import PendingPlacementCard from './pendingPlacementCard';
import TransferredInPlacementCard from './transferredInPlacementCard';
import ActivePlacementCard from './activePlacementCard';
import associateProfileService from 'src/services/associateProfile/associateProfileService';
import { useAppSelector } from 'src/store/store';
import { useParams } from 'react-router-dom';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';

type AssociateProfilePlacementsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'success'; data: AssociateProfilePlacementsResponse };

const DEFAULT_PLACEMENT_STATE: AssociateProfilePlacementsState = {
  status: 'loading',
} as const;

const AssociateProfilePlacements = () => {
  const { alias } = useParams();
  const { selectedSite } = useAppSelector((state) => state.sites);
  const [placementsState, setPlacementsState] = useState<AssociateProfilePlacementsState>(DEFAULT_PLACEMENT_STATE);
  useEffect(() => {
    const fetchAssociateProfilePlacements = async () => {
      setPlacementsState({ status: 'loading' });
      if (!alias || selectedSite.id === 0) {
        setPlacementsState({ status: 'error' });
        return;
      }

      try {
        const { data } = await associateProfileService.getAssociateProfilePlacements(alias, selectedSite.id);
        setPlacementsState({ status: 'success', data });
      } catch (e) {
        setPlacementsState({ status: 'error' });
      }
    };

    fetchAssociateProfilePlacements();
  }, [alias, selectedSite.id]);

  if (placementsState.status === 'loading') {
    return <LoadingSpinner />;
  }

  if (placementsState.status === 'error') {
    return <Text>No placements</Text>;
  }

  return (
    <Column spacing="400">
      <Text type="b500">Placements Pending Action ({placementsState.data.pending.length})</Text>
      {placementsState.data.pending.map((placement) => (
        <PendingPlacementCard key={placement.accommodationId} placement={placement} />
      ))}
      {placementsState.data.transferredIn.map((placement) => (
        <TransferredInPlacementCard key={placement.accommodationId} placement={placement} />
      ))}
      <Text type="b500">Active Placements ({placementsState.data.active.length})</Text>
      {placementsState.data.active.map((placement) => (
        <ActivePlacementCard key={placement.accommodationId} placement={placement} />
      ))}
    </Column>
  );
};

export default AssociateProfilePlacements;
